import { P2GBox, P2GLink, P2GTooltip, P2GText } from '@parcel2go/core-ui';
import { RoyalMailProvider } from '~/lib/consts/providers';
import { QuoteApiModel, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import useProviderName from '../../../hooks/use-provider-name';
import { CalculatedTotal, getCalculatedTotal, getSelectedQuoteOptionOrDefault } from '../generateProtectionKey';
import { QuoteProviderCache } from '../../../lib/cache/QuoteProviderCache';
import { toDisplayCurrency } from '~/components/currency/toDisplayCurrency';
import { TranslationNamespaces } from '~/lib/constants';
import { Trans, useTranslation } from 'react-i18next';
import { useTaxName } from '@parcel2go/npm-p2g-app';
import { useMemo } from 'react';
import ItemBreakdown from './ItemBreakdown';

interface props {
  item: SmartSendItemApiModel;
  quote: QuoteApiModel;
  variant?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'subtitle1' | 'subtitle2' | 'body1' | 'body2';
}

const ToolTipMessage = ({ currencyCode, q }: { currencyCode: string; q: CalculatedTotal }) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const taxName = useTaxName();

  const defaults: { translationKey: string; default: string } = useMemo(() => {
    if (!q) {
      return null;
    }

    if (q.Duties && q.TotalVat) {
      return {
        translationKey: `ITEM_TOTAL_SUMMARY_ALL`,
        default: `Sub Total: {{subTotal}} <br /> {{taxName}}: {{vat}} <br /> Duties and Taxes: {{duties}} <br />---<br />Total Price: {{total}}`
      };
    }

    if (q.Duties) {
      return {
        translationKey: `ITEM_TOTAL_SUMMARY_ONLY_DUTIES`,
        default: `Sub Total: {{subTotal}} <br /> Duties and Taxes: {{duties}} <br />---<br />Total Price: {{total}}`
      };
    }

    if (q.TotalVat) {
      return {
        translationKey: `ITEM_TOTAL_SUMMARY_ONLY_TAX`,
        default: `Sub Total: {{subTotal}} <br /> {{taxName}}: {{vat}} <br />---<br />Total Price: {{total}}`
      };
    }

    return {
      translationKey: `ITEM_TOTAL_SUMMARY`,
      default: `Sub Total: {{subTotal}} <br />---<br />Total Price: {{total}}`
    };
  }, [q]);

  if (!defaults) {
    return null;
  }

  return (
    <Trans
      t={t}
      i18nKey={defaults.translationKey}
      values={{
        subTotal: toDisplayCurrency(currencyCode, q.TotalPriceExVat),
        vat: toDisplayCurrency(currencyCode, q.TotalVat),
        duties: toDisplayCurrency(currencyCode, q.Duties),
        total: toDisplayCurrency(currencyCode, q.TotalPrice),
        taxName: taxName
      }}
      defaults={defaults.default}
    />
  );
};

export default function ItemTotal(props: props) {
  const providerName = useProviderName();
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const pn = props.quote?.Service?.Provider;
  const taxName = useTaxName();

  if (pn === RoyalMailProvider) {
    return (
      <P2GText variant="body2">
        {QuoteProviderCache.accounts[props.quote.Service.AccountId] || providerName(pn)}
      </P2GText>
    );
  }

  if (!props.quote || !props.quote.Options || props.quote.Options.length == 0) {
    return null;
  }

  const q = getCalculatedTotal(getSelectedQuoteOptionOrDefault(props.item, props.quote));

  return q ? (
    <P2GTooltip
      variant="light"
      title={
        <ItemBreakdown item={props.item}>
          <P2GBox textAlign="right">
            <P2GText component="span" small>
              <ToolTipMessage currencyCode={props.quote.CurrencyCode} q={q} />
            </P2GText>
          </P2GBox>
        </ItemBreakdown>
      }
    >
      <P2GText variant={props.variant}>
        {q.TotalVat === 0 ? (
          <P2GLink>{toDisplayCurrency(props.quote.CurrencyCode, q.TotalPriceExVat)}</P2GLink>
        ) : (
          <Trans
            t={t}
            i18nKey={`ITEM_TOTAL_EXC_TAX`}
            components={{ a: <P2GLink />, small: <P2GText variant={props.variant} small /> }}
            values={{
              total: toDisplayCurrency(props.quote.CurrencyCode, q.TotalPriceExVat),
              taxName: taxName
            }}
            defaults={`<a>{{total}}</a> <small>exc {{taxName}}</small>`}
          />
        )}
      </P2GText>
    </P2GTooltip>
  ) : null;
}
